import { useState, useEffect } from 'react'
import { Settings2, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/v2/ui'
import { useAuditImageDefaults } from '@/hooks/useAuditImageDefaults'
import { useAuditModelConfig } from '@/hooks/useAuditModelConfig'

export function AuditImageSettings() {
  const { t } = useTranslation()
  const { defaults, saveDefaults, isSaving } = useAuditImageDefaults()
  const { ratioOptions, resolutionOptions } = useAuditModelConfig()
  const [open, setOpen] = useState(false)
  const [local, setLocal] = useState(defaults)

  useEffect(() => { setLocal(defaults) }, [defaults])

  const handleSave = () => {
    saveDefaults(local)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="p-2 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-orange-400 dark:hover:border-orange-500 transition-colors text-neutral-500 hover:text-orange-500"
          title={t('amazonListing.imageSettings.title')}
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">
          {t('amazonListing.imageSettings.title')}
        </h4>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {t('amazonListing.imageSettings.description')}
        </p>
        <SettingsSelect
          label={t('amazonListing.slots.resolution')}
          value={local.resolution}
          options={resolutionOptions.map(r => ({ value: r, label: r.toUpperCase() }))}
          onChange={v => setLocal(prev => ({ ...prev, resolution: v }))}
        />
        <SettingsSelect
          label={t('amazonListing.slots.aspectRatio')}
          value={local.aspect_ratio}
          options={ratioOptions.map(r => ({ value: r, label: r }))}
          onChange={v => setLocal(prev => ({ ...prev, aspect_ratio: v }))}
        />
        <SettingsSelect
          label={t('amazonListing.slots.countLabel')}
          value={String(local.generation_count)}
          options={[1, 2, 3, 4].map(n => ({ value: String(n), label: String(n) }))}
          onChange={v => setLocal(prev => ({ ...prev, generation_count: Number(v) }))}
        />
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
        >
          <Check className="h-3.5 w-3.5" />
          {t('amazonListing.imageSettings.save')}
        </button>
      </PopoverContent>
    </Popover>
  )
}

function SettingsSelect({ label, value, options, onChange }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="text-sm px-2.5 py-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}
